import {
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ClearRounded,
  PlaylistAddRounded,
  UpdateRounded
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import { Revision, getRevisionsAsync } from '../Revision/revisionSlice';
import {
  createPurposeAsync,
  getPurposeAsync,
  updatePurposeAsync
} from './purposeSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    row: {
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    columnLeft: {
      width: '50%',
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginRight: 0
      }
    },
    columnRight: {
      width: '50%',
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginLeft: 0
      }
    },
    columnFullWidth: {
      width: '100%',
      marginBottom: theme.spacing(2)
    }
  })
);

interface CreateEditPurpose {
  id: number | null;
  abbreviation: string | null;
  fullName: string | null;
  phase: string | null;
  revisions: Revision[];
}

export default function PurposeCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedPurpose } = useSelector((state: RootState) => state.purposes);
  const { data: revisions } = useSelector(
    (state: RootState) => state.revisions
  );

  const params = useParams<{ purposeId: string }>();
  const purposeId = Number.parseInt(params.purposeId, 10);
  const isNewPage = !Boolean(purposeId);

  const [purpose, setPurpose] = useState<CreateEditPurpose>({
    id: null,
    abbreviation: null,
    fullName: null,
    phase: null,
    revisions: []
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedPurpose) {
      setPurpose({
        ...purpose,
        id: selectedPurpose.id,
        abbreviation: selectedPurpose.abbreviation,
        fullName: selectedPurpose.fullName,
        phase: selectedPurpose.phase,
        revisions: selectedPurpose.revisions
      });
    }
  }, [selectedPurpose]);

  useEffect(() => {
    if (!isNewPage) dispatch(getPurposeAsync(purposeId));
    dispatch(getRevisionsAsync());
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setPurpose({
      ...purpose,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (purpose.abbreviation && purpose.fullName && purpose.phase) {
        if (isNewPage) {
          // create purpose
          const createPurpose = {
            abbreviation: purpose.abbreviation,
            fullName: purpose.fullName,
            phase: purpose.phase,
            revisionIds: purpose.revisions.map((revision) => revision.id)
          };
          await dispatch(createPurposeAsync(createPurpose)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('messages.success.saved')
            })
          );
        } else {
          // update purpose
          const updatePurpose = {
            id: purpose.id,
            abbreviation: purpose.abbreviation,
            fullName: purpose.fullName,
            phase: purpose.phase,
            revisionIds: purpose.revisions.map((revision) => revision.id)
          };
          await dispatch(updatePurposeAsync(updatePurpose)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('messages.success.updated')
            })
          );
        }
      } else {
        throw new Error(t('messages.error.somethingWentWrong') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('masterData.purpose.title'),
          masterDataRef: 'purposes',
          masterDataSubName: selectedPurpose?.abbreviation,
          masterDataSubRef: selectedPurpose?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage
          ? t('masterData.purpose.newTitle')
          : t('masterData.purpose.editTitle')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('masterData.purpose.info')}
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.row}>
            <TextField
              className={classes.columnLeft}
              id="abbreviation"
              label={t('masterData.purpose.fields.abbreviation')}
              size="small"
              variant="outlined"
              name="abbreviation"
              fullWidth
              value={purpose.abbreviation ? purpose.abbreviation : ''}
              onChange={onChangeField}
              required
            />
            <TextField
              className={classes.columnRight}
              id="purpose-full-name"
              label={t('masterData.purpose.fields.fullName')}
              size="small"
              variant="outlined"
              name="fullName"
              fullWidth
              value={purpose.fullName ? purpose.fullName : ''}
              onChange={onChangeField}
              required
            />
          </div>
          <TextField
            className={classes.columnFullWidth}
            id="phase"
            label={t('masterData.purpose.fields.phase')}
            size="small"
            variant="outlined"
            name="phase"
            fullWidth
            value={purpose.phase ? purpose.phase : ''}
            onChange={onChangeField}
            required
          />
          <Autocomplete
            id="revisions"
            size="small"
            multiple
            disableCloseOnSelect
            options={revisions}
            getOptionLabel={(option) => option?.name}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  variant="outlined"
                  size="small"
                  deleteIcon={<ClearRounded />}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t('masterData.purpose.fields.revision')}
              />
            )}
            value={purpose.revisions}
            getOptionSelected={(option, value) => option.id === value?.id}
            onChange={(event, newValue) =>
              setPurpose({
                ...purpose,
                revisions: newValue
              })
            }
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage
            ? t('masterData.purpose.add')
            : t('projectInfoCard.update')}
        </Button>
      </form>
    </div>
  );
}
